import { Button, Flex, Spacer, Switch, Text, Textarea, VisuallyHidden } from '@chakra-ui/react';
import { EMBED, EMBED_TOGGLE } from 'constants/mixpanel';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useAppSelector } from 'modules/Editor/store';
import { selectInfographSize, selectInfographTitle } from 'modules/Editor/store/infographSelector';
import { UserTypes, useUser } from 'modules/User';
import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UpgradeButton } from '../../UpgradeButton';
import { HandleClickUpgrade } from '../DownloadModal/DownloadModal.types';
import { UpgradeLock } from '../DownloadModal/OptionTitle';
import { useToastHelpers } from '../useToastHelpers';

interface EmbedSwitchesProps {
  disabled: boolean;
  onCopyEmbedCode: (embedCode: string) => void;
  onUpgrade?: HandleClickUpgrade;
}

interface SwitchStates {
  footer: boolean;
  border: boolean;
  responsive: boolean;
}

enum SwitchName {
  Footer = 'footer',
  Border = 'border',
  Responsive = 'responsive',
}
const switchStyles = {
  '.chakra-switch__track': {
    bg: 'gray.500',
  },
  _checked: {
    '.chakra-switch__track': {
      bg: 'upgrade.500',
    },
  },
};

export const EmbedPanel = ({ handleClickUpgrade }: { handleClickUpgrade: () => void }) => {
  const { userType } = useUser();
  const { t } = useTranslation('common_navbar');
  const { copyTextToClipboard } = useToastHelpers({
    successToast: t('modals.share-panel.toast.success-embed-copy-link'),
    failToast: t('modals.share-panel.toast.failure-embed-copy-link'),
  });

  const handleCopyEmbedCode = useCallback(
    (embedCode: string) => {
      copyTextToClipboard(embedCode);

      // Mixpanel event
      Mixpanel.track(EMBED.eventName, {
        'User Plan': userType,
        share_method: EMBED.shareMethod,
        from: EMBED.from,
        location: EMBED.location,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userType, copyTextToClipboard],
  );

  return (
    <Flex flexDir={'column'} w='full' mt='2' gap='2' justifyContent={'space-between'}>
      <EmbedSwitches disabled={false} onCopyEmbedCode={handleCopyEmbedCode} onUpgrade={handleClickUpgrade} />
    </Flex>
  );
};

const EmbedSwitches = ({ disabled, onCopyEmbedCode, onUpgrade }: EmbedSwitchesProps) => {
  const [switchStates, setSwitchStates] = useState<SwitchStates>({
    // manage all state in one setState
    footer: true,
    border: false,
    responsive: true,
  });

  const [embedCode, setEmbedCode] = useState('');
  const { venngageE2 } = useVenngageE1Gate();
  const { isFreeUser, userType } = useUser();

  const size = useAppSelector(selectInfographSize);
  const title = useAppSelector(selectInfographTitle);
  const widthPx = size.widthPx;
  const heightPx = size.heightPx;
  const dataVgID = venngageE2?.publicLinkId;

  const { t } = useTranslation('common_navbar');
  const id = useId();

  // handle all switch state
  const handleSwitchChange = (switchName: keyof SwitchStates) => {
    setSwitchStates((prevState) => {
      const updatedState = {
        ...prevState,
        [switchName]: !prevState[switchName],
      };

      // Mixpanel event tracks which are toggled
      Mixpanel.track(EMBED_TOGGLE.eventName, {
        'User Plan': userType,
        embed_option_footer: updatedState.footer,
        embed_option_border: updatedState.border,
        embed_option_responsive: updatedState.responsive,
        width: widthPx,
        height: heightPx,
        title: title,
        from: EMBED_TOGGLE.from,
        location: EMBED.location,
      });

      return updatedState;
    });
  };

  useEffect(() => {
    const embedDomain = window.location.origin;

    let scriptEl =
      `<script src="${embedDomain + '/js/embed/v1/embed.js'}" ` +
      `data-vg-id="${dataVgID}" ` +
      `data-title="${title}" ` +
      `data-w="${widthPx}" ` +
      `data-h="${heightPx}"` +
      ` data-multipage="true"`;
    if (!switchStates.footer) scriptEl += ' data-f="false"';
    if (switchStates.border) scriptEl += ' data-b="true"';
    if (!switchStates.responsive) scriptEl += ' data-fixed="true"';

    scriptEl += '></script>';

    setEmbedCode(scriptEl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchStates.footer, switchStates.border, switchStates.responsive, setEmbedCode, widthPx, heightPx]);

  return (
    <Flex flexDir='column' gap='2'>
      <Flex flexDir='row' justifyContent='space-between'>
        <Text
          fontWeight={disabled ? 'medium' : 'semibold'}
          fontSize='sm'
          lineHeight='1.25'
          color={disabled ? 'font.300' : 'font.500'}
        >
          {t('embed.options')}
        </Text>
        {isFreeUser ? (
          <Flex flexDir='row' justifyContent='space-between' alignItems='center' color='font.200'>
            <UpgradeLock
              userType={UserTypes.Premium}
              handleClickUpgrade={onUpgrade}
              upgradeText={t('modals.download.upgrade-premium')}
            />
          </Flex>
        ) : null}
      </Flex>
      <Flex flexDir='column' gap='2'>
        <Spacer />
        <Flex flexDir='row' justifyContent='space-between' flexGrow={1}>
          <Text fontSize='sm' lineHeight='1.25' color={'font.300'} as={'label'} htmlFor='footer-switch'>
            {t('embed.footer')}
          </Text>
          <Switch
            id='footer-switch'
            sx={{ ...switchStyles }}
            disabled={isFreeUser}
            isChecked={switchStates.footer}
            onChange={() => handleSwitchChange(SwitchName.Footer)}
          />
        </Flex>
        <Flex flexDir='row' justifyContent='space-between' flexGrow={1}>
          <Text fontSize='sm' lineHeight='1.25' color={'font.300'} as={'label'} htmlFor='border-switch'>
            {t('embed.border')}
          </Text>
          <Switch
            id='border-switch'
            sx={{ ...switchStyles }}
            disabled={isFreeUser}
            isChecked={switchStates.border}
            onChange={() => handleSwitchChange(SwitchName.Border)}
          />
        </Flex>
        <Flex flexDir='row' justifyContent='space-between' flexGrow={1}>
          <Text fontSize='sm' lineHeight='1.25' color={'font.300'} as={'label'} htmlFor='responsive-switch'>
            {t('embed.responsive')}
          </Text>
          <Switch
            id='responsive-switch'
            sx={{ ...switchStyles }}
            disabled={isFreeUser}
            isChecked={switchStates.responsive}
            onChange={() => handleSwitchChange(SwitchName.Responsive)}
          />
        </Flex>
      </Flex>
      <Flex flexDir='column' mt={'2'} gap='4'>
        {!isFreeUser ? (
          <Text
            fontWeight={disabled ? 'medium' : 'semibold'}
            fontSize='sm'
            lineHeight='1.25'
            color={disabled ? 'font.300' : 'font.500'}
          >
            {t('embed.code')}
          </Text>
        ) : null}

        <VisuallyHidden>
          <Text as='label' htmlFor={id}>
            {t('embed.code')}
          </Text>
        </VisuallyHidden>
        <Textarea
          id={id}
          disabled
          resize={'none'}
          colorScheme='gray.500'
          value={embedCode}
          size={'xs'}
          wordBreak={'break-all'}
          hidden={isFreeUser}
        />
        {isFreeUser ? (
          <UpgradeButton userType={userType} renderFrom='embed-panel' />
        ) : (
          <Button onClick={() => onCopyEmbedCode(embedCode)} variant='action' size='sm' w='100%' h={9}>
            {t('embed.copyEmbedCode')}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
